//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';

import Button from '@components/atoms/button/Button';
import {
  FlatPrimaryButtonStyles,
  IButtonStateStyles,
} from '@components/atoms/button/buttonStyles';
import Text, { MetatagText } from '@components/atoms/text/Text';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';

export interface ManageLabelsTableHeaderCellProps
  extends Pick<React.ComponentProps<typeof Text>, 'justifyContent'> {
  isActive: boolean;
  isAscending: boolean;
  label: string;
  minWidth: string;
  onChange: (isAscending: boolean) => void;
}

export const ManageLabelsTableHeaderCell = ({
  isActive,
  isAscending,
  justifyContent,
  label,
  minWidth,
  onChange,
}: ManageLabelsTableHeaderCellProps) => {
  const arrowButtonStyles: IButtonStateStyles = {
    ...FlatPrimaryButtonStyles,
    active: {
      color: theme.colors.black,
      backgroundColor: theme.colors.gray0,
      borderColor: 'inherit',
    },
    fixed: {
      ...FlatPrimaryButtonStyles.fixed,
      borderShape: 'circle',
    },
    hover: {
      color: theme.colors.black,
      backgroundColor: theme.colors.gray0,
      borderColor: 'inherit',
    },
    standard: {
      ...FlatPrimaryButtonStyles.standard,
      color: isActive ? theme.colors.gray9 : theme.colors.gray3,
    },
  };

  return (
    <Cell justifyContent={justifyContent} minWidth={minWidth}>
      {label}
      <ArrowButton
        aria-label={`Sort by ${label} in ${isAscending ? 'ascending' : 'descending'} order`}
        buttonStyles={arrowButtonStyles}
        iconName={isAscending ? 'chevron-up' : 'chevron-down'}
        onClick={() => onChange(!isAscending)}
        trackingId={ContentDrawerTrackingIds.SORT_LABELS}
      />
    </Cell>
  );
};

const Cell = styled(MetatagText)<{ minWidth: string }>(
  ({ theme, minWidth }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacings[1]};
    min-width: ${minWidth};
  `,
);

const ArrowButton = styled(Button)(
  ({ theme }) => `
    background-color: ${theme.colors.gray0};
    border-color: ${theme.colors.gray0};
    box-shadow: none;
  `,
);
